import { array, mixed, object, string } from 'yup'
import type { ProjectFieldDTO } from '@api/fastAPI.schemas'
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_OPTION_LABEL_LENGTH,
  MAX_OPTION_VALUE_LENGTH,
  MAX_TITLE_LENGTH,
  RESERVED_TEMPLATE_KEYS,
  TITLE_PATTERN,
  normalizeTitle,
  templateKey,
} from './utils'

export interface ProjectFieldOptionFormValues {
  id?: string | null
  label: string
  value: string
}

export interface ProjectFieldFormValues {
  field_type: ProjectFieldDTO['field_type']
  title: string
  description: string
  options: ProjectFieldOptionFormValues[]
}

/**
 * @param takenKeys template keys of the organisation's other fields (lower case) -- titles must
 * stay unique, since each one names a placeholder.
 */
export const buildProjectFieldSchema = (takenKeys: string[]) =>
  object({
    field_type: mixed<ProjectFieldDTO['field_type']>().oneOf(['text', 'select']).required(),
    title: string()
      .required('Bitte einen Titel eingeben')
      .test(
        'title-length',
        `Maximal ${MAX_TITLE_LENGTH} Zeichen`,
        value => normalizeTitle(value ?? '').length <= MAX_TITLE_LENGTH,
      )
      .test('title-pattern', 'Nur Buchstaben A–Z (ohne Umlaute und ß) und Leerzeichen', value =>
        TITLE_PATTERN.test(normalizeTitle(value ?? '')),
      )
      .test(
        'title-reserved',
        'Dieser Titel ist bereits als fester Platzhalter der Projektdaten vergeben',
        value => !RESERVED_TEMPLATE_KEYS.includes(templateKey(value ?? '').toLowerCase()),
      )
      .test(
        'title-unique',
        'Es gibt bereits ein Feld mit diesem Titel',
        value => !takenKeys.includes(templateKey(value ?? '').toLowerCase()),
      ),
    description: string()
      .trim()
      .required('Bitte eine Beschreibung eingeben')
      .max(MAX_DESCRIPTION_LENGTH, `Maximal ${MAX_DESCRIPTION_LENGTH} Zeichen`),
    options: array()
      .of(
        object({
          label: string()
            .trim()
            .required('Bitte eine Bezeichnung eingeben')
            .max(MAX_OPTION_LABEL_LENGTH, `Maximal ${MAX_OPTION_LABEL_LENGTH} Zeichen`),
          value: string()
            .trim()
            .required('Bitte einen Wert eingeben')
            .max(MAX_OPTION_VALUE_LENGTH, `Maximal ${MAX_OPTION_VALUE_LENGTH} Zeichen`),
        }),
      )
      .when('field_type', {
        is: 'select',
        then: schema =>
          schema
            .min(1, 'Ein Auswahlfeld braucht mindestens eine Option')
            .test(
              'unique-labels',
              'Die Bezeichnungen der Optionen müssen eindeutig sein',
              options => {
                const labels = (options ?? []).map(option =>
                  (option.label ?? '').trim().toLowerCase(),
                )
                return new Set(labels).size === labels.length
              },
            ),
      }),
  })
